import moment from 'moment';
import { Fragment, PureComponent, ReactNode } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { GlobalState } from '../../../store/reducers';
import { PatientDetailsVisits } from '../../../store/reducers/prescriptionPad';
import {
    getPatientMedicalHistory,
    getPatientDetailsVisits,
    isMHFilled,
} from '../../../store/selectors/prescriptionPad';
import { MHC, MHItem2, MEDICAL_HISTORY_CATEGORY } from '../../../store/types/medicalHistory';
import { configs } from './MagicBoxConfigs';
import { MAGIC_BOX_COMPONENTS } from './MAGIC_BOX_COMPONENTS';
import { changePathAndFocus } from './magicBoxUtils';
import MHEmpty from './MHEmpty';
import { updatePPPrivateNotes } from '../../../store/actions/prescriptionPad';
import { LOCAL_ID_PREFIX } from '../../../constants';
import { randomString } from '../../../store/utils';
import { ThunkDispatch } from 'redux-thunk';
import { Actions } from '../../../store/actions';
import { notification } from 'antd';
import { Notes } from '../../../store/types/prescription';
import { RouterProps } from './routerProps';
import Wrapper from './Wrapper';

interface Props extends RouteComponentProps<RouterProps> {
    dispatch: ThunkDispatch<GlobalState, null, Actions>;
    mh?: MHC;
    visits?: PatientDetailsVisits;
}

class MH extends PureComponent<Props> {
    onMHButtonClick = () => {
        const { match, history } = this.props;
        const { pxId } = match.params;
        changePathAndFocus(
            configs[MAGIC_BOX_COMPONENTS.MEDICAL_HISTORY].elId,
            history,
            pxId,
            'edit',
        );
    };

    onCopyPrivateNotes = (privateNotes: Partial<Notes>) => {
        const { dispatch } = this.props;

        if (!privateNotes.text) {
            return;
        }

        dispatch(
            updatePPPrivateNotes({
                id: `${LOCAL_ID_PREFIX}${randomString(4)}`,
                text: privateNotes.text,
                parsedText: privateNotes.parsedText || privateNotes.text,
            }),
        );

        notification.success({
            message: `Copied to Pad`,
            duration: 5,
            placement: 'bottomRight',
        });
    };

    render(): ReactNode {
        const { mh, visits } = this.props;
        const privateNotes = visits?.data
            ?.filter((t) => t.privateNotes?.text)
            .map((t) => ({ ...t.privateNotes, date: t.date }));
        const mhFilled = mh && isMHFilled(mh);

        if (!mhFilled && (!visits || !privateNotes?.length)) {
            return (
                <div className="mt-48">
                    <MHEmpty handleCta={this.onMHButtonClick} />
                </div>
            );
        }

        return (
            <div className="magicbox-history-container">
                <div
                    onClick={this.onMHButtonClick}
                    className="p-16 px-12 max-h-3/5 h-3/5 overflow-auto scroll-hidden cursor-pointer"
                >
                    {mh && mhFilled ? (
                        <div className="flex flex-wrap gap-6 text-14">
                            {Object.keys(mh)
                                .reduce((allMh, key): (MHItem2 & { catKey: keyof MHC })[] => {
                                    if (
                                        (key as keyof MHC) !==
                                            MEDICAL_HISTORY_CATEGORY.RECENT_TRAVEL_HISTORY &&
                                        (key as keyof MHC) !==
                                            MEDICAL_HISTORY_CATEGORY.OTHER_MEDICAL_HISTORY
                                    ) {
                                        mh[key as keyof MHC] &&
                                            allMh.push(
                                                ...(mh[key as keyof MHC] as MHItem2[]).map(
                                                    (item) => ({
                                                        ...item,
                                                        catKey: key as keyof MHC,
                                                    }),
                                                ),
                                            );
                                    }
                                    return allMh;
                                }, [] as (MHItem2 & { catKey: keyof MHC })[])
                                .filter((t) => t.status?.toLowerCase() === 'active')
                                .map((item) => (
                                    <p
                                        key={item.id}
                                        className={`px-8 py-6 mb-7 border border-darwin-accent-medical-history-orange-500 text-darwin-accent-medical-history-orange-900 bg-darwin-accent-medical-history-orange-100
                                        rounded-4`}
                                    >
                                        {item.name}
                                    </p>
                                ))}
                        </div>
                    ) : (
                        <div className="mb-24">
                            <MHEmpty handleCta={this.onMHButtonClick} />
                        </div>
                    )}
                </div>

                {!!privateNotes?.length && (
                    <div className="bg-darwin-neutral-100 max-h-2/5 h-2/5 overflow-auto hide-scrollbar font-lato font-400">
                        <div className="m-14 mx-16 space-y-6">
                            <p className="text-11 text-darwin-neutral-600">PRIVATE NOTES</p>
                            {privateNotes.map((t) => {
                                return (
                                    <div className="magicbox-pastvisit">
                                        <Wrapper
                                            iconCssClass=""
                                            iconText={undefined}
                                            content={
                                                <Fragment key={t?.id}>
                                                    <div
                                                        className="flex flex-col tiny-mce"
                                                        dangerouslySetInnerHTML={{
                                                            __html: t?.text || t?.parsedText || '',
                                                        }}
                                                    />
                                                    <p className="text-12 text-darwin-neutral-700 italic">
                                                        {moment(t?.date).format("DD MMM'YY")}
                                                    </p>
                                                </Fragment>
                                            }
                                            onCopy={() => {
                                                this.onCopyPrivateNotes(t);
                                            }}
                                            iconTooltipContent={undefined}
                                            isWholeBodyCopy={false}
                                        ></Wrapper>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default connect((state: GlobalState) => ({
    mh: getPatientMedicalHistory(state),
    visits: getPatientDetailsVisits(state),
}))(withRouter(MH));
